import { useMutation, useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import { map } from 'ramda';
import React, { FC, useContext } from 'react';

import LoadingWrapper from '@ui/atoms/LoadingWrapper';

import {
	IAuthenticatedMember,
	ICancelationReason,
	ICancelationReasons,
	IMemberSubscription,
} from '@hooks/useMemberInfo';

import { keysToCamel } from '@utils/butter';
import { combineMemberFragments } from '@utils/graphql';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IPageProps } from '@folx-types/flow-steps';

import {
	cancelationReasons,
	cancelationReasonsName,
	queryName,
} from '@graphql/fragments/cancelationReasons';
import CREATE_MEMBER_CANCELLATION_REASONS from '@graphql/mutations/createMemberCancellationReasons.mutation.gql';

import CancelationSurvey from './CancelationSurvey.component';
import { ICancelationSurveyPageContent } from './types';

interface ICancelationSurveyPageProps extends IPageProps {}

const CancelationSurveyPage: FC<ICancelationSurveyPageProps> = ({ path }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "cancelation-survey" }) {
				page_header
				page_info
				additional_info_header
				additional_info_placeholder
				cancelation_survey_content {
					key
					value
				}
			}
		}
	`);

	const { client: graphqlV2Client } = useContext(GraphQLv2Context);
	const { data, loading } = useQuery<
		IAuthenticatedMember<IMemberSubscription<ICancelationReasons>>
	>(combineMemberFragments([cancelationReasonsName], [cancelationReasons], queryName), {
		client: graphqlV2Client,
	});

	const [cancelationReasonMutation] = useMutation(CREATE_MEMBER_CANCELLATION_REASONS, {
		client: graphqlV2Client,
	});

	const refinedContent: ICancelationSurveyPageContent = keysToCamel(
		pageContent
	) as ICancelationSurveyPageContent;

	const cancelReasonData =
		data?.authenticatedMember?.membershipSubscription?.cancellationReasons?.reasons ?? [];

	const butterKeys = map<ICancelationReason, string>((reason) => {
		return reason.butterKey;
	}, cancelReasonData);

	return (
		<LoadingWrapper loading={loading}>
			<CancelationSurvey
				pageContent={refinedContent}
				cancelationReasons={butterKeys}
				cancelationReasonData={cancelReasonData}
				path={path}
				cancelationReasonMutation={cancelationReasonMutation}
			/>
		</LoadingWrapper>
	);
};

export default CancelationSurveyPage;
